import { Component } from '@angular/core'

interface TransparencyItem {
    title: string
    severity: 'info' | 'warning' | 'risk'
    status: 'known' | 'mitigated' | 'todo' | 'aligned' | 'confirm'
    /** Fee Share Agreement wording the item relates to */
    agreement?: string
    description: string
    impact: string
    recommendation?: string
}

interface TransparencySection {
    name: string
    icon: string
    intro?: string
    link?: { label: string; route: string }
    items: TransparencyItem[]
}

@Component({
    selector: 'app-admin-transparency',
    templateUrl: './admin-transparency.component.html',
    styleUrls: ['./admin-transparency.component.scss'],
})
export class AdminTransparencyComponent {
    sections: TransparencySection[] = [
        {
            name: 'Admyt Integration',
            icon: 'fa-solid fa-shield-halved',
            items: [
                {
                    title: 'Parker email not always sent to Admyt',
                    severity: 'warning',
                    status: 'known',
                    description: 'When creating a Parker, the email field is optional. The Admyt API requires an email when creating a tenant user. If a parker has no email, we send an empty string to Admyt.',
                    impact: 'Admyt may have users with blank emails, which could cause issues with their notification system or create duplicate records if the same blank email is used for multiple users.',
                    recommendation: 'Consider falling back to the client account email when a parker has no email set. This ensures Admyt always receives a valid email address.',
                },
                {
                    title: 'Admyt tenant user not deleted when parker is deleted',
                    severity: 'warning',
                    status: 'known',
                    description: 'When a parker is deleted from the Parkers page, the system triggers a full client re-sync. However, the re-sync only creates/verifies users -- it does not remove Admyt users whose corresponding parker has been deleted.',
                    impact: 'Deleted parkers may still have active Admyt access until the subscription ends or an admin manually removes them via the Audit page.',
                    recommendation: 'The delete flow should explicitly call deleteAdmytTenantUser for the parker being deleted, rather than relying on re-sync.',
                },
                {
                    title: 'No real-time sync on parker name changes',
                    severity: 'info',
                    status: 'known',
                    description: 'If a parker\'s name is updated in ParkUpp, the corresponding Admyt tenant user\'s name is not automatically updated. The Admyt API does not have an "update user" endpoint -- only create and delete.',
                    impact: 'Name mismatches between ParkUpp and Admyt. The Audit page will show the correct names on both sides but they may differ.',
                    recommendation: 'Accept this as a limitation of the Admyt API. Name changes would require deleting and recreating the Admyt user.',
                },
                {
                    title: 'Subscription parkerRefs not populated',
                    severity: 'info',
                    status: 'mitigated',
                    description: 'The migration did not update subscription documents with parkerRefs (to avoid triggering thousands of listener events). Subscriptions still have the old occupantRefs or no refs at all.',
                    impact: 'No functional impact. The audit page and re-sync tool load ALL parkers for a client directly, bypassing parkerRefs entirely. The subscription creation flow correctly sets parkerRefs for new subscriptions.',
                    recommendation: 'No action needed. The system works without parkerRefs on legacy subscriptions.',
                },
                {
                    title: 'Old occupants collection still exists',
                    severity: 'info',
                    status: 'mitigated',
                    description: 'The migration copies vehicles from clients/{uid}/occupants to clients/{uid}/parkers/{id}/vehicles/{id} but does not delete the old occupants. Both collections exist in Firestore.',
                    impact: 'No functional impact. The frontend no longer reads from the old occupants path. The data remains as a backup. Firestore storage cost is negligible.',
                    recommendation: 'Consider a cleanup migration in the future to remove old occupants after confirming the new system is stable in production.',
                },
                {
                    title: 'Rate limiting on Admyt API',
                    severity: 'warning',
                    status: 'mitigated',
                    description: 'The Admyt API has rate limits. When running the Audit page for all centers, or syncing many clients at once, we can hit 429 (Too Many Requests) errors.',
                    impact: 'Mitigated with exponential backoff retry (up to 4 retries with 1s, 2s, 4s, 8s delays). The Audit page also adds delays between API calls. However, very large operations may still occasionally fail.',
                    recommendation: 'Always run audits per-center rather than all centers at once. Use the re-sync tool for individual clients rather than bulk operations.',
                },
                {
                    title: 'Admyt tenant created per subscription, not per client',
                    severity: 'info',
                    status: 'known',
                    description: 'Each ParkUpp subscription creates a separate Admyt tenant (company). If a client has multiple subscriptions at the same building, they will have multiple tenants in Admyt.',
                    impact: 'This is by design and matches how Admyt structures their data. Each tenant has its own bay allocation and users.',
                    recommendation: 'No change needed. This is the correct behavior.',
                },
                {
                    title: 'Deactivated Admyt tenants return 404 on user queries',
                    severity: 'info',
                    status: 'mitigated',
                    description: 'When an Admyt tenant is deactivated (subscription ended), querying its users returns a 404 error. The Audit page skips user fetching for inactive tenants to avoid these errors.',
                    impact: 'No functional impact. The Audit shows inactive tenants greyed out without user details. The 404 errors are handled gracefully and not logged as critical alerts.',
                },
                {
                    title: 'No webhook from Admyt for status changes',
                    severity: 'risk',
                    status: 'known',
                    description: 'ParkUpp pushes data to Admyt but does not receive notifications when Admyt makes changes (e.g., a building manager deactivates a user directly in Admyt). We only discover these discrepancies when running the Audit.',
                    impact: 'If Admyt deactivates a user or tenant outside of ParkUpp, our system will still show them as active. The Audit page is the only way to detect this.',
                    recommendation: 'Run the Audit regularly (daily or weekly) to catch discrepancies. Consider requesting webhook support from Admyt for future integration.',
                },
                {
                    title: 'Reactivate tenant endpoint available but not auto-triggered',
                    severity: 'info',
                    status: 'known',
                    description: 'We implemented the Admyt "reactivate tenant" API endpoint. The re-sync tool uses it when it detects an inactive tenant that should be active. However, it is not automatically triggered when a new subscription is created for a parking that previously had a deactivated tenant.',
                    impact: 'If a client gets a new subscription at a building where they previously had a deactivated tenant, the re-sync tool or Audit page must be used to reactivate it. The automatic flow creates a new tenant instead.',
                },
            ],
        },
        {
            name: 'Affiliate Programme: Fee Share Agreement alignment',
            icon: 'fa-solid fa-file-signature',
            intro:
                'How the affiliate build lines up with the Fee Share Agreement (Referral / Introducer Arrangement) the client uses. "Aligned" items follow the agreement as written. "Needs sign-off" items are assumptions we made where the agreement is silent or the system needs a firm rule: please confirm or tell us what to change. Nothing here has been paid out yet.',
            link: { label: 'Affiliate Programme Guide', route: '/admin/affiliate-guide' },
            items: [
                {
                    title: 'Fee share = 50% of Trading Income',
                    severity: 'info',
                    status: 'aligned',
                    agreement: 'Fee Share: 50% of Trading Income. Trading Income: what ParkUpp actually earns on a booking, after paying the landlord’s agreed rental and deducting the Banking Fee.',
                    description:
                        'Each paid invoice is converted to the rental excl. VAT (invoice ÷ 1.15). ParkUpp’s fee is the portfolio’s commission rate on that rental; the banking fee is deducted; the affiliate earns 50% of what is left (never below R0). The agreement’s worked example (R1,000 rental, 15% fee, 3.5% banking fee) gives R57.50 in the system, matching the agreement exactly.',
                    impact: 'The 50% is the default for every new affiliate and can be changed per affiliate if a different rate is signed.',
                },
                {
                    title: 'Banking fee is a flat 3.5% of the rental excl. VAT',
                    severity: 'warning',
                    status: 'confirm',
                    agreement:
                        'Banking Fee: any bank or payment-processing charge ParkUpp incurs on the transaction — payment gateway fees (e.g. PayFast, PayGate), debit order charges, or any other bank charge. The worked example uses 3.5%.',
                    description:
                        'ParkUpp doesn’t record the actual bank charge on each invoice, so the system deducts 3.5% of the rental excl. VAT on every invoice, whatever the payment method (card, PayFast, EFT). The rate used is stored on every fee share line, so it can be audited.',
                    impact: 'EFT payments are probably cheaper than 3.5% and card payments may cost more, so individual fee shares may be slightly lower or higher than a strict reading of "actual charges".',
                    recommendation:
                        'Confirm a flat 3.5% is acceptable (and consider saying so in the agreement), or give us a rate per payment method. Per-method rates are a small change; recording the actual charge per transaction is a larger one.',
                },
                {
                    title: 'Building introductions are per building, 12 months from first listing',
                    severity: 'info',
                    status: 'aligned',
                    agreement:
                        'Building Introduction — you earn your Fee Share on the Trading Income from every bay in that Building, for 12 months from the date it’s first listed. This applies building-by-building: introducing one Building doesn’t automatically extend to that landlord’s other properties.',
                    description:
                        'Each building is credited individually and earns on every bay in it, whoever books, for 12 months from the date it was first published. The system now records each building’s first listing date. Buildings listed before this was built have no recorded date; when admins credit one they enter the go-live date (or it defaults to the date the building was created).',
                    impact: 'Other buildings in the same portfolio do not earn unless they are credited too (see the next item).',
                },
                {
                    title: 'A landlord introduction auto-credits buildings listed within 90 days',
                    severity: 'warning',
                    status: 'confirm',
                    agreement: 'Building-by-building: introducing one Building doesn’t automatically extend to that landlord’s other properties.',
                    description:
                        'When an introduced landlord joins ParkUpp (signs up through the affiliate link and creates a portfolio, or accepts our invite), the system records a "landlord introduction". It earns nothing by itself. Buildings that landlord first lists within 90 days are treated as the introduced building(s) and credited automatically. Buildings listed later are not credited unless an admin does it.',
                    impact:
                        'We assume the buildings a new landlord lists in their first 90 days are the ones the affiliate introduced. If a landlord lists several unrelated buildings at once, all of them would be credited.',
                    recommendation: 'Confirm the 90-day rule, or choose: credit only the first building automatically, or credit nothing automatically (admins credit each building by hand).',
                },
                {
                    title: 'Parker introductions: 12 months from first booking',
                    severity: 'info',
                    status: 'aligned',
                    agreement:
                        'Parker Introduction — you earn your Fee Share on the Trading Income actually generated from that Parker’s booking(s), for up to 12 months from the date they first book. If the booking runs for less than 12 months, the Fee Share applies only to the Trading Income actually earned.',
                    description:
                        'The 12 months start at the parker’s first booking, not at sign-up. Only invoices actually paid earn; a booking that ends early simply stops producing paid invoices.',
                    impact: 'None.',
                },
                {
                    title: '“First booking” means the first booking that gets paid',
                    severity: 'info',
                    status: 'confirm',
                    agreement: '…for up to 12 months from the date they first book.',
                    description:
                        'A booking request can be rejected by the landlord or abandoned before payment. The system starts the 12 months at the date of the first booking that results in a paid invoice (using the booking’s creation date). Abandoned or rejected requests don’t start the clock.',
                    impact: 'Slightly favours the affiliate compared with counting a rejected request as the first booking.',
                    recommendation: 'Confirm.',
                },
                {
                    title: 'Same affiliate introduced the building and the parker: paid once',
                    severity: 'info',
                    status: 'aligned',
                    agreement:
                        'Single Fee Share payment — if you introduce both a Building and a Parker who then books a bay in that same Building, you will receive a single, consolidated Fee Share … but not both.',
                    description: 'Each paid invoice can produce only one fee share record. When the same affiliate holds both claims, they are paid once.',
                    impact: 'None.',
                },
                {
                    title: 'Different affiliates introduced the building and the parker: building wins',
                    severity: 'risk',
                    status: 'confirm',
                    agreement: 'The agreement only covers the same person introducing both. It says nothing about two different referring parties.',
                    description:
                        'At ParkUpp’s request, when one affiliate introduced the building and another introduced the parker, the building’s affiliate is paid and the parker’s affiliate is not (to avoid paying twice on one booking). Every such case is recorded and shown on the Overlap report.',
                    impact:
                        'Under the agreement as written, the parker’s introducer could argue they are owed their fee share on that parker’s bookings, because nothing in their agreement says a building introduction overrides it.',
                    recommendation:
                        'Add a clause such as: “Where another party is entitled to a Fee Share on the same booking as a Building Introduction, the Building Introduction takes priority and no Parker Introduction Fee Share is payable on that booking.” Or tell us to pay both.',
                },
                {
                    title: 'Statements monthly, paid by the 25th, no minimum',
                    severity: 'info',
                    status: 'aligned',
                    agreement:
                        'Each month, ParkUpp will send a statement showing the Trading Income and Fee Share calculation. Payment is made by the 25th of each month, by EFT to your nominated bank account.',
                    description:
                        'An admin generates the statements each month (on Accounts → Affiliate Payouts; this is deliberately not automatic): the previous month is closed and one statement is created per affiliate, due by the 25th. There is no minimum and nothing for the affiliate to request. Each line shows the rental, ParkUpp fee, banking fee and Trading Income. Admins pay by EFT and record the reference.',
                    impact: 'If nobody generates the statements, affiliates aren’t paid by the 25th. The statement is visible on the affiliate’s page and in admin; it is not emailed yet (see Known gaps).',
                },
                {
                    title: 'Ending the agreement doesn’t stop earned fee shares',
                    severity: 'info',
                    status: 'aligned',
                    agreement:
                        'Ending the Agreement doesn’t affect Fee Shares already earned — those keep being paid for the rest of their 12-month Qualifying Period. ParkUpp may end it immediately for a material breach not fixed within 14 days.',
                    description:
                        'Setting an affiliate to Terminated stops new introductions, but introductions already made keep earning for the rest of their 12 months and keep appearing on statements. For a breach, admins remove the specific introductions with a note.',
                    impact: 'None.',
                },
                {
                    title: 'Rates fixed at the time of each transaction',
                    severity: 'info',
                    status: 'aligned',
                    agreement: 'If ParkUpp’s fee or the Banking Fee change generally, Trading Income is calculated using the rates that actually applied to that transaction at the time.',
                    description: 'The ParkUpp fee %, banking fee % and fee share % are frozen on each line when the invoice is paid. Later rate changes only affect later payments.',
                    impact: 'None.',
                },
                {
                    title: 'Landlords with a blank fee rate produce no fee share',
                    severity: 'warning',
                    status: 'confirm',
                    agreement: 'Trading Income: what ParkUpp actually earns on a booking, after paying the landlord’s agreed rental…',
                    description:
                        'Portfolios created through landlord self-signup have a blank commission rate, and today the landlord payout gives those landlords 100% of the rent. ParkUpp therefore earns nothing on them, so Trading Income is R0 and no fee share is paid. (Portfolios with no rate field at all are treated as 15%, the same as the payout.)',
                    impact: 'An affiliate who introduced a self-signup landlord earns nothing until a rate is set on that portfolio.',
                    recommendation:
                        'Decide whether blank rates are intentional. If not, set the rate on those portfolios (this also changes what the landlord is paid). The Landlord & building introductions tab shows which portfolios have no rate.',
                },
                {
                    title: 'Introductions: “Effective Cause” and written notice',
                    severity: 'info',
                    status: 'aligned',
                    agreement:
                        'You must be the Effective Cause … not just an incidental connection. Tell ParkUpp in writing about any introduction you make. ParkUpp will decide, reasonably and in good faith, whether a Building or Parker qualifies.',
                    description:
                        'Affiliate links create introductions automatically as evidence, and admins can add, change or remove any introduction by hand with a note. Every manual change is kept in the introduction’s history.',
                    impact: 'Automatic link tracking is a convenience; ParkUpp’s decision (recorded by an admin) is what counts.',
                },
                {
                    title: 'New parkers and new landlords only',
                    severity: 'info',
                    status: 'aligned',
                    description:
                        'Confirmed by ParkUpp (29 Oct 2026), for every type of affiliate: introducing someone who already parks with ParkUpp, or is already registered, does not count. Same for landlords. Links only credit accounts created in the last 7 days and portfolios less than 90 days old. Manual credits by an admin are refused if the parker’s account, or the landlord’s portfolio, is more than 90 days old.',
                    impact: 'An affiliate cannot earn on existing ParkUpp parkers or landlords, even if an admin tries to credit them.',
                    recommendation: 'Confirm 90 days is the right cut-off for manual credits (it matches how long a link is remembered).',
                },
                {
                    title: 'Link tracking rules',
                    severity: 'info',
                    status: 'confirm',
                    description:
                        'A link is remembered in the browser for 90 days; the first affiliate link clicked wins; only accounts created in the last 7 days can be introduced by link; the affiliate can’t introduce themselves. The agreement doesn’t specify these.',
                    impact: 'Introductions that fall outside these rules have to be added by hand.',
                    recommendation: 'Confirm, or give us different windows.',
                },
                {
                    title: 'VAT on the fee share',
                    severity: 'warning',
                    status: 'confirm',
                    description:
                        'The agreement doesn’t say whether the fee share includes VAT. The system pays the calculated amount as is. Affiliates can record a VAT number with their bank details, but VAT is not added.',
                    impact: 'VAT-registered affiliates may expect to invoice VAT on top.',
                    recommendation: 'Confirm whether the fee share is VAT-inclusive, and whether ParkUpp issues self-billing statements or the affiliate invoices ParkUpp.',
                },
            ],
        },
        {
            name: 'Affiliate Programme: security guards',
            icon: 'fa-solid fa-user-shield',
            intro: 'Security guards are an informal kind of affiliate: no login, created by an admin, paid a flat bounty per parker instead of a fee share. These are the rules we assumed. Please confirm or tell us what to change.',
            link: { label: 'Affiliate Programme Guide', route: '/admin/affiliate-guide' },
            items: [
                {
                    title: 'R100 per parker, once that parker has paid 3 invoices',
                    severity: 'info',
                    status: 'confirm',
                    description:
                        '"Parking for more than three months" is measured as three paid invoices since the guard introduced the parker (most bookings are invoiced monthly). The bounty is created automatically when the third invoice is paid. Both the amount and the number of invoices can be set per guard; defaults are R100 and 3.',
                    impact: 'A parker who prepays several months on one invoice would take longer to qualify; a parker who cancels before 3 payments earns the guard nothing.',
                    recommendation:
                        'ParkUpp confirmed one payout once the parker has parked for more than three months. Still to confirm: is "3 paid invoices" the right test, or should it count months of parking from the subscription dates?',
                },
                {
                    title: 'One payout per parker, then the relationship ends',
                    severity: 'info',
                    status: 'aligned',
                    description:
                        'Confirmed by ParkUpp (29 Oct 2026): a guard gets a single payout for each parker who parks for more than three months, and nothing further for that parker. The system allows at most one bounty per parker, however long they keep parking or however many bays they book.',
                    impact: 'Guards are paid for bringing the person, not per booking or per bay.',
                },
                {
                    title: 'Guard bounty and building fee share can both be paid on the same parker',
                    severity: 'warning',
                    status: 'confirm',
                    description:
                        'If a guard brings a parker to a building whose landlord was introduced by another affiliate, the building affiliate still earns their fee share on every invoice and the guard still earns their one-off bounty. The guard bounty does not take the place of the fee share, and vice versa.',
                    impact: 'On those parkers ParkUpp pays both the fee share and the R100 bounty.',
                    recommendation: 'Confirm this is acceptable, or tell us which should win.',
                },
                {
                    title: 'No clawback once a bounty is due',
                    severity: 'info',
                    status: 'confirm',
                    description:
                        'Once a bounty is due it stays due, even if one of the qualifying invoices is later refunded. Admins can cancel a due bounty by hand (with a reason); a paid bounty cannot be undone in the system.',
                    impact: 'A refund after the bounty is due needs an admin decision.',
                },
                {
                    title: 'Guards earn only for parkers',
                    severity: 'info',
                    status: 'aligned',
                    description:
                        'If someone a guard sent goes on to list parking as a landlord, the guard is not credited with the landlord or their buildings, and admins cannot credit buildings to a guard. Change the affiliate to a different type first if that has been agreed separately.',
                    impact: 'None.',
                },
                {
                    title: 'Guards are paid outside the monthly statements',
                    severity: 'info',
                    status: 'aligned',
                    description:
                        'Guards have no bank details or statements in the system. Bounties are listed under Affiliate Payouts → Guard bounties; an admin pays by cash, airtime or EFT and records the method and reference.',
                    impact: 'Nothing is sent to the guard automatically. Tell them when a bounty is paid.',
                },
            ],
        },
        {
            name: 'Affiliate Programme: known gaps',
            icon: 'fa-solid fa-handshake',
            link: { label: 'Affiliate Programme Guide', route: '/admin/affiliate-guide' },
            items: [
                {
                    title: 'Portfolios set up by ParkUpp need a landlord introduction recorded by hand',
                    severity: 'warning',
                    status: 'known',
                    description:
                        'If ParkUpp creates and runs a portfolio and the landlord never logs in, nothing links it to the affiliate automatically.',
                    impact: 'Its buildings earn nothing until credited. A building’s 12 months still run from its first listing, so late crediting loses time.',
                    recommendation: 'Record the landlord introduction (or credit the building) on the day the portfolio is set up. A "Referred by affiliate" picker on the admin create-landlord form would remove this step.',
                },
                {
                    title: 'Late crediting needs an Integrity check run',
                    severity: 'warning',
                    status: 'known',
                    description:
                        'A fee share is worked out when an invoice is paid. If a building or parker is credited after some of their invoices were paid, those invoices aren’t revisited automatically.',
                    impact: 'Invoices paid before the credit was added (but inside the 12 months) are missed until the Integrity check is run with "Fix issues". Invoices that already have a fee share record for another claim are not re-decided.',
                    recommendation: 'Run the Integrity check after crediting anything late and before paying each month’s statements.',
                },
                {
                    title: 'Referral capture needs the same browser',
                    severity: 'info',
                    status: 'known',
                    description: 'The affiliate code is remembered in the browser that opened the link. Signing up on another device, or after clearing the browser, loses it.',
                    impact: 'Some genuine introductions need to be added by hand.',
                },
                {
                    title: 'Marking an invoice paid creates a fee share',
                    severity: 'warning',
                    status: 'known',
                    description: 'Fee shares are calculated the moment an invoice becomes "paid" and reversed when it stops being paid, including manual status changes on the Invoices page.',
                    impact: 'Mistaken status changes create and reverse ledger entries. A reversal after the statement was paid becomes a clawback on the next statement.',
                    recommendation: 'Only mark invoices paid when money was received.',
                },
                {
                    title: 'Statements are not emailed yet',
                    severity: 'warning',
                    status: 'todo',
                    description: 'The agreement says ParkUpp will send a monthly statement. Statements are created and visible on the affiliate’s page and in admin, but no email or WhatsApp is sent.',
                    impact: 'Admins must send the statement (or point the affiliate to their page) until notifications are built.',
                    recommendation: 'Next phase: email the statement when it is generated and a payment confirmation when marked paid.',
                },
                {
                    title: 'Link visit counts are indicative only',
                    severity: 'info',
                    status: 'known',
                    description: 'Visits are counted once per browser per code per day. Anyone can open a link repeatedly from new browsers.',
                    impact: 'Visit numbers can be inflated. They never affect money.',
                },
            ],
        },
    ]

    getSeverityClass(severity: string): string {
        switch (severity) {
            case 'risk': return 'badge bg-danger'
            case 'warning': return 'badge bg-warning text-dark'
            case 'info': return 'badge bg-info text-dark'
            default: return 'badge bg-secondary'
        }
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'mitigated': return 'badge bg-success'
            case 'known': return 'badge bg-warning text-dark'
            case 'todo': return 'badge bg-danger'
            case 'aligned': return 'badge bg-success'
            case 'confirm': return 'badge bg-primary'
            default: return 'badge bg-secondary'
        }
    }
}
